import device from 'current-device';
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import ScrollTrigger from 'gsap/ScrollTrigger';

import { MagneticCaption } from './magnetic-caption.js';
import OverlayCaptionsIterator from './overlay-caption.js';
import MagneticIterator from './magnetic.js';

gsap.registerPlugin(SplitText, ScrollTrigger);

export default class Fake3dSlider {
  constructor(state) {
    this.isMobile = device.mobile() || device.tablet();

    this.isSlider = state.apps.length > 1;

    this.state = state;
    this.stateTransition = {
      pixelRatio: 1.5,
      renderSimultaneously: true,
      autoPausePlay: true,
      pauseTimeout: 500,
      stretchMultiplier: {
        x: 0.01,
        y: 1,
      },
      innerTimeMultiplier: 0.0002,
      transitionVelocity: 0.015,
      transitionEasing: 'quadraticInOut',
      stretchAmplitude: 0,
      transitionTime: 0,
    };

    this.elem = document.querySelector('.b3-slider');
    this.overlay = this.elem.querySelector('.b3-slider__overlay-gradient');
    this.preloader = this.elem.querySelector('.b3-slider__preloader');
    this.poster = this.elem.querySelector('.b3-slider__poster--default');
    this.posterBlur = this.elem.querySelector('.b3-slider__poster--blur');
    this.top = this.elem.querySelector('.b3-slider__top');
    this.topLine = this.elem.querySelector('.b3-slider__top-line');
    this.loader = this.elem.querySelector('.b3-slider__loader');
    this.loaderCircle = this.elem.querySelector('.b3-slider__loader-circle');
    this.loaderText = this.elem.querySelector('.b3-slider__loader-text');
    this.content = this.elem.querySelector('.b3-slider__content');
    this.slideList = this.elem.querySelectorAll('.b3-slider__slide');
    this.overlaysList = this.elem.querySelectorAll('.b3-slider__overlay');
    this.controls = this.elem.querySelector('.b3-slider__controls');
    this.controlsButtonNext = this.elem.querySelector('.b3-slider__controls-button--next');
    this.controlsButtonPrev = this.elem.querySelector('.b3-slider__controls-button--prev');
    this.arrowButtonPrev = this.elem.querySelector('.b3-slider__arrow--prev');
    this.arrowButtonNext = this.elem.querySelector('.b3-slider__arrow--next');
    this.counter = this.elem.querySelector('.b3-slider__counter');
    this.counterCurrent = this.elem.querySelector('.b3-slider__counter-current');
    this.counterTotal = this.elem.querySelector('.b3-slider__counter-total');
    this.logosList = this.elem.querySelectorAll('.b3-slider__logo');
    this.header = document.querySelector('.s-header');

    this.currentId = 0;
    this.lastId = this.state.apps.length - 1;
    this.disabledControl = false;
    this.initedScene = false;
    this.errorScene = false;
    this.activeCursor = false;
    this.isAutoShowArrow = false;
    this.isActive = true;
    this.videoPlayed = false;
    this.timeoutAutoShowHud = null;

    this.classSlideActive = 'b3-slider__slide--visible';
    this.classOverlayActive = 'b3-slider__overlay--visible';
    this.classCursorActive = 'b3-slider__cursor--active';
    this.classCursorPrev = 'b3-slider__cursor--prev';
    this.classNameControlsMuted = 'b3-slider__controls--muted';
    this.classNameArrowMuted = 'b3-slider__arrow--muted';

    this.selectorTitle = '.b3-slider__title';
    this.selectorText = '.b3-slider__text';
    this.selectorLink = '.b3-slider__link';
    this.selectorOverlayCaption = '.b3-slider__info';
    this.selectorMagnetic = '.b3-slider__magnetic';
    this.selectorHeaderOverlay = '.b3-slider__overlay span';

    this.common = {
      isMobile: this.isMobile,
      overlay: this.overlay,
    };

    new OverlayCaptionsIterator(this.common);
    if (!this.isMobile) new MagneticIterator();

    this.preInit();

    this.state.apps.forEach((state, index) => {
      this.setSlideTheme(state, index);
    });
  }

  setSlideTheme(state, index) {
    const currentSlide = this.slideList[index];
    const currentOverlay = this.overlaysList[index];

    currentSlide.style.setProperty('--slider-text-color', state.overlays.text);
    
    currentSlide.style.setProperty('--slider-local-point-color', state.overlays.localPoint.color);
    currentSlide.style.setProperty('--slider-local-point-text', state.overlays.localPoint.text);

    currentOverlay.style.setProperty('--slider-overlay-1-color', state.overlays.overlay1.color);
    currentOverlay.style.setProperty('--slider-overlay-1-mode', state.overlays.overlay1.mode);
    currentOverlay.style.setProperty('--slider-overlay-1-deg', state.overlays.overlay1.deg + 'deg');

    currentOverlay.style.setProperty('--slider-overlay-2-gradient-1-color', state.overlays.overlay2.gradient1.color);
    currentOverlay.style.setProperty('--slider-overlay-2-gradient-1-deg', state.overlays.overlay2.gradient1.deg + 'deg');
    currentOverlay.style.setProperty('--slider-overlay-2-gradient-2-color-1', state.overlays.overlay2.gradient2.color1);
    currentOverlay.style.setProperty('--slider-overlay-2-gradient-2-color-2', state.overlays.overlay2.gradient2.color2);
    currentOverlay.style.setProperty('--slider-overlay-2-gradient-2-deg', state.overlays.overlay2.gradient2.deg + 'deg');
    currentOverlay.style.setProperty('--slider-overlay-2-mode', state.overlays.overlay2.mode);
    
    currentOverlay.style.setProperty('--slider-overlay-header', state.overlays.header);

    if (!index) {
      this.changeSlideTheme(state);
    }
  }

  changeSlideTheme(state) {
    this.elem.style.setProperty('--slider-global-point-color1', state.overlays.globalPoint.color1);
    this.elem.style.setProperty('--slider-global-point-color2', state.overlays.globalPoint.color2);
    this.elem.style.setProperty('--slider-global-point-deg', state.overlays.globalPoint.deg + 'deg');
    this.elem.style.setProperty('--slider-global-point-text', state.overlays.globalPoint.text);
    this.elem.style.setProperty('--scene-filter', state.overlays.filter);
  }

  preInit() {
    gsap.set(this.header, {
      display: 'none',
      opacity: 0,
      transition: 'none',
      willChange: 'transform',
    });
    gsap.set(this.content, { visibility: 'hidden'});

    if (this.isSlider) {
      gsap.set(this.arrowButtonPrev, { display: 'none'});
      gsap.set(this.counter, { perspective: 2000 });
    }

    if (this.poster.complete) {
      this.loadScene();
    } else {
      this.poster.addEventListener('load', () => {
        this.loadScene();
      });
    }

    this.initSplitting();
    this.startAnimationTl = this.startAnimation().pause();

    ScrollTrigger.create({
			trigger: this.elem,
			start: 'top bottom',
			end: 'bottom top',
      onEnter: () => {
        this.isActive = true;
        this.toggleRender();
      },
      onEnterBack: () => {
        this.isActive = true;
        this.toggleRender();
      },
      onLeave: () => {
        this.isActive = false;
        this.toggleRender();
      },
		});
  }

  toggleRender() {
    if (this.initedScene) {
      if (this.isActive) {
        if (!this.videoPlayed) {
          this.scene.playVideo();
          this.videoPlayed = true;
        }

        this.scene.startRender();
      } else {
        this.scene.stopRender();
      }
    }
  }

  hidePreloader() {
    return new Promise((resolve) => {
      gsap.to(this.preloader, {
        opacity: 0,
        ease: 'power1.inOut',
        duration: 0.4,
        onComplete: () => {
          gsap.set(this.preloader, { display: 'none' });
          this.startAnimationTl.play();
          resolve(true);
        },
      });
    });
  }

  async loadScene() {
    await this.hidePreloader();

    const script = document.createElement('script');
    const path = window.prefixPath || '';
    script.src = path + '/js/module_antialiased.js';
    script.onload = () => {
      this.initScene(); 
    };
    script.onerror = () => {
      this.errorScene = true;
    };
    document.head.appendChild(script);
  }

  initScene() {
    if (this.isSlider) {
      this.counterTotal.innerHTML = this.slideList.length.toString().padStart(2, '0');
    }

    const config = [];
    this.state.apps.forEach((item) => {
      config.push({
        type: item.type,
        state: item,
      });
    });

    const container = document.querySelector('.container-slider')

    this.scene = new window.SliderModule.App(
      container,
      config,
      this.stateTransition,
    );

    window.addEventListener('resize', () => {
      this.scene.resize();
    });
    
    if (this.scene.apps[0].dvideo) {
      this.scene.apps[0].dvideo.addEventListener('loadeddata', () => {
        this.startScene();
      });
    } else {
      this.startScene();
    }
  }

  startScene() {
    this.scene.initializeScene();

    this.scene.apps.forEach((slide) => {
      if (slide.svgs?.length) {
        slide.svgs.forEach((svg, index) => {
          const path = window.prefixPath || '';
          slide.setSVGCanvasFromFile(`${path}/svgs-${slide.state.name}/Svg${index}.svg`, index);
        });
      }

      if (slide.type === 'image' && slide.state.customImage) {
        const pathD = `/images/${slide.state.customImage}/d.jpeg`;
        const pathC = `/images/${slide.state.customImage}/c.jpeg`;
    
        slide.loadTexture(pathD, (t) => {
          slide.textureD = t;
          slide.textureData.vdtex = t;
          slide.updateMaterialTexture();
          slide.updateMaterialRegime();
        });
        slide.loadTexture(pathC, (t) => {
          slide.textureC = t;
          slide.textureData.vctex = t;
          slide.updateMaterialTexture();
          slide.updateMaterialRegime();
        });
      }
    });
  
    if (this.isActive) {
      this.scene.animate();
      this.scene.playVideo();
      this.videoPlayed = true;
    }

    this.initedScene = true;

    const delay = this.startAnimationEnded ? 0 : 1;
    this.hidePoster(delay);
    this.autoShowHud();
    if (this.isSlider) this.initNavigation();
  }

  startAnimation() {
    return this.animationSlide(true);
  }

  hidePoster(delay = 0) {
    const tl = gsap.timeline();

    tl.to([this.poster, this.posterBlur], {
      opacity: 0,
      ease: 'power1.inOut',
      duration: 0.8,
      delay: delay,
      overwrite: true,
      onStart: () => {
        this.setLoaderProgress(100);
      },
      onComplete: () => {
        this.scene.apps.forEach((slide) => {
          if (slide.type === 'image' && slide.state.customImage) {
            slide.textureData.vdtex = slide.textureD;
            slide.textureData.vctex = slide.textureC;
            slide.updateMaterialTexture();
            slide.updateMaterialRegime();
          }
        });
      }
    });

    if (this.isSlider) {
      tl.to([this.controls, this.arrowButtonNext, this.arrowButtonPrev], {
        pointerEvents: '',
        opacity: 1,
        ease: 'power1.inOut',
        duration: 0.4,
      });
    }

    return tl;
  }

  autoShowHud() {
    if (this.scene.apps[this.currentId].type === 'hud' && !this.scene.apps[this.currentId].autoShowed) {
      this.timeoutAutoShowHud = setTimeout(() => {
        const id = this.currentId;
        this.scene.apps[id].autoShowed = true;
        if (this.scene.apps[id].setGlobalBoxAnimationTargetTime) {
          this.scene.apps[id].setGlobalBoxAnimationTargetTime(1);
  
          setTimeout(() => {
            this.scene.apps[id].setGlobalBoxAnimationTargetTime(0);
          }, 8000);
        }
      }, 6000);
    }
  }

  initSplitting() {
    this.splitting = [];
    this.slideList.forEach((slide, indexSlide) => {
      const title = slide.querySelector(this.selectorTitle);
      const splitTitle = new SplitText(title, {
        type: 'chars, words',
      });

      const text = slide.querySelector(this.selectorText);
      let splitText = null;

      if (text) {
        splitText = new SplitText(text, {
          type: 'words',
          tag: 'span',
        });
      }

      this.splitting.push({
        title: splitTitle,
        text: splitText,
      });

      const magnetic = slide.querySelectorAll(this.selectorMagnetic);
      if (magnetic.length) {
        magnetic.forEach((item) => {
          item.setAttribute('data-reverse-x', this.state.apps[indexSlide].reverseX);
          item.setAttribute('data-reverse-y', this.state.apps[indexSlide].reverseY);
          new MagneticCaption(this.common, item);
        });
      }
    });
  }

  initNavigation() {
    this.controlsButtonNext?.addEventListener('click', () => {
      this.nextSlide();
    });
    this.controlsButtonPrev?.addEventListener('click', () => {
      this.prevSlide();
    });

    this.arrowButtonNext?.addEventListener('click', () => {
      this.nextSlide();
      if (!this.isMobile && !this.isAutoShowArrow) {
        this.initAutoShowArrow();
      }
    });
    this.arrowButtonPrev?.addEventListener('click', () => {
      this.prevSlide();
    });

    window.addEventListener('keydown', (event) => {
      switch (event.code) {
        case 'ArrowRight':
          this.nextSlide();
          break;
        case 'ArrowLeft':
          this.prevSlide();
          break;
        default:
          break;
      }
    });
  }

  initAutoShowArrow() {
    this.isAutoShowArrow = true;

    gsap.set(this.arrowButtonPrev, { display: '', opacity: 0 });

    this.elem.addEventListener('mousemove', (e) => {
      const rightLimit = Math.max(this.elem.offsetWidth - this.elem.offsetWidth / 3, this.arrowButtonNext.offsetWidth);
      const leftLimit = Math.max(this.elem.offsetWidth / 3, this.arrowButtonNext.offsetWidth);
    
      const isRight = e.clientX > rightLimit;
      const isLeft = e.clientX < leftLimit;

      if (isRight || isLeft) {
        const arrow = isRight ? this.arrowButtonNext : this.arrowButtonPrev;
        
        gsap.to(arrow, {
          opacity: 1,
          ease: 'power1.out',
          duration: 1,
        });
      } else {
        gsap.to([this.arrowButtonNext, this.arrowButtonPrev], {
          opacity: 0,
          ease: 'power1.out',
          duration: 1,
        });
      }
    });
  }

  nextSlide() {
    if (!this.disabledControl) {
      this.disabledControl = true;
      setTimeout(() => this.disabledControl = false, 1000);

      this.removeActiveSlide();
      this.scene.forward();

      if (this.currentId === this.lastId) {
        this.currentId = 0;
      } else {
        this.currentId++;
      }

      if (this.scene.apps[this.currentId].dvideo) {
        this.scene.apps[this.currentId].setVideoTime(0);
      }

      this.setActiveSlide();
    }
  }

  prevSlide() {
    if (!this.disabledControl) {
      this.disabledControl = true;
      setTimeout(() => this.disabledControl = false, 1000);
  
      this.removeActiveSlide();
      this.scene.backward();
  
      if (this.currentId === 0) {
        this.currentId = this.lastId;
      } else {
        this.currentId--;
      }
  
      if (this.scene.apps[this.currentId].dvideo) {
        this.scene.apps[this.currentId].setVideoTime(0);
      }
  
      this.setActiveSlide();
    }
  }

  removeActiveSlide() {
    this.slideList[this.currentId].classList.remove(this.classSlideActive);
    this.overlaysList[this.currentId].classList.remove(this.classOverlayActive);
  }

  setActiveSlide() {
    clearTimeout(this.timeoutAutoShowHud);
    this.timeoutAutoShowHud = null;
    this.autoShowHud();

    this.changeSlideTheme(this.state.apps[this.currentId]);
    
    this.slideList[this.currentId].classList.add(this.classSlideActive);
    this.overlaysList[this.currentId].classList.add(this.classOverlayActive);

    let split = new SplitText(this.counterCurrent, { type: 'chars' });

    gsap.to(split.chars, {
      opacity: 0,
      yPercent: 100,
      rotationX: -90,
      stagger: 0.02,
      ease: 'power2.inOut',
      duration: 0.4,
      onComplete: () => {
        this.counterCurrent.innerHTML = (this.currentId + 1).toString().padStart(2, '0');
        split = new SplitText(this.counterCurrent, { type: 'chars' });

        gsap.fromTo(split.chars, {
          opacity: 0,
          yPercent: -100,
          rotationX: -90,
        }, {
          opacity: 1,
          yPercent: 0,
          rotationX: 0,
          stagger: 0.08,
          ease: 'expo.out',
          duration: 0.7,
        });
      }
    });

    this.animationSlide();
  }

  setLoaderProgress(value, delay = 0) {
    return new Promise((resolve) => {
      gsap.to(this.topLine, {
        xPercent: -100 + value,
        ease: 'power1.inOut',
        duration: 0.7,
        delay: delay,
        onComplete: () => {
          if (value === 100) {
            this.hideLoader();
            resolve(true);
          }
        },
      });
    });
  }

  setLoaderProgressFake() {
    gsap.to(this.posterBlur, {
      opacity: 0.7,
      ease: 'power1.out',
      duration: 1,
    });

    this.setLoaderProgress(30, 0.5);
    
    setTimeout(() => {
      if (!this.initedScene) {
        this.setLoaderProgress(65, 0.7);
        gsap.to(this.posterBlur, {
          opacity: 0.35,
          ease: 'power1.inOut',
          duration: 1,
        });
        gsap.to(this.loaderText, {
          opacity: 1,
          ease: 'power1.inOut',
          duration: 0.5,
          delay: 1.5,
        });

        setTimeout(() => {
          if (!this.initedScene) {
            this.setLoaderProgress(100);
          }
        }, 14000);
      }
    }, 3500);
  }

  hideLoader() {
    gsap.to([this.top, this.loader], {
      opacity: 0,
      ease: 'power1.inOut',
      duration: 0.5,
      overwrite: true,
      onComplete: () => {
        gsap.set(this.top, { visibility: '' });
        gsap.set(this.loader, { visibility: '' });
      },
    });
  }

  animationSlide(start = false) {
    const tl = new gsap.timeline();

    const delatStart = start ? 0.4 : 0;
    const delay = start ? delatStart + 0.5 : 0.3;
    const slide = this.slideList[this.currentId];
    const overlay = this.overlaysList[this.currentId];
    const splitting = this.splitting[this.currentId];
    const staggerTitle = 0.02;
    const staggerText = 0.02;
    const durationCharsTitle = splitting.title.chars.length * staggerTitle;
    const durationCharsText = durationCharsTitle + splitting.text?.words.length * staggerText;
    const info = slide.querySelector(this.selectorOverlayCaption);
    const magnetics = slide.querySelectorAll(this.selectorMagnetic);
    const link = slide.querySelector(this.selectorLink);
    const headerOverlay = overlay.querySelector(this.selectorHeaderOverlay);

    if (start) {
      tl.set(this.header, { display: '' }, delatStart);
      tl.set(headerOverlay, { display: 'block' }, delatStart);

      tl.fromTo(headerOverlay, {
        yPercent: -50,
      }, {
        yPercent: 0,
        ease: 'power1.out',
        duration: 0.6,
      }, delatStart);

      tl.fromTo(this.header, {
        opacity: 0,
        yPercent: -50,
      }, {
        opacity: 1,
        yPercent: 0,
        ease: 'power1.out',
        duration: 0.6,
        onComplete: () => {
          gsap.set(this.header, { transition: '' });
        }
      }, delatStart);

      tl.to(this.posterBlur, {
        opacity: 0.9,
        ease: 'power1.inOut',
        duration: 1,
      });

      if (this.isSlider) {
        tl.set([this.controls, this.arrowButtonNext, this.arrowButtonPrev], {
          visibility: 'visible',
          pointerEvents: 'none',
        }, 0);
    
        tl.fromTo(this.controls, {
          opacity: 0,
          y: 20,
        }, {
          opacity: () => !this.initedScene && !this.errorScene ? 0.5 : 1,
          y: 0,
          ease: 'power2.out',
          duration: 0.6,
        }, delay + durationCharsText + 0.4);
    
        tl.fromTo([this.arrowButtonNext, this.arrowButtonPrev], {
          opacity: 0,
          scale: 0.7,
        }, {
          opacity: () => !this.initedScene && !this.errorScene ? 0.5 : 1,
          scale: 1,
          ease: 'power2.out',
          duration: 0.6,
          clearProps: 'transform',
          onComplete: () => {
            this.startAnimationEnded = true;
          },
        }, delay + durationCharsText + 0.4);
      }

      if (this.logosList.length) {
        tl.fromTo(this.logosList, {
          opacity: 0,
          yPercent: 10,
        }, {
          opacity: 1,
          yPercent: 0,
          stagger: 0.05,
          ease: 'power1.inOut',
          duration: 0.8,
        }, delay + durationCharsText + 0.6);
      }

      tl.add(() => {
        if (!this.initedScene && !this.errorScene) {
          gsap.set(this.topLine, { xPercent: -100 });
          gsap.set(this.top, { visibility: 'visible' });
          gsap.set(this.loader, { visibility: 'visible' });
          gsap.to(this.loaderCircle, {
            opacity: 1,
            ease: 'power1.inOut',
            duration: 0.5,
          });
          this.setLoaderProgressFake();
        }
      }, delay + durationCharsText + 1.5);
    }

    tl.set(splitting.title?.words, { perspective: 2000 }, 0);
    
    tl.set(this.content, { visibility: 'visible' }, delay);
    tl.fromTo(splitting.title.chars, {
      opacity: 0,
      rotationX: -90,
    }, 
    { 
      opacity: 1,
      rotationX: 0,
      stagger: staggerTitle,
      ease: 'expo.out',
      duration: 1.5,
    }, delay);

    if (splitting.text) {
      tl.fromTo(splitting.text.words, {
        opacity: 0,
        yPercent: 20,
      }, 
      { 
        opacity: 1,
        yPercent: 0,
        stagger: staggerText,
        ease: 'power1.out',
        duration: 0.7,
      }, delay + durationCharsTitle);
    }

    if (link) {
      tl.fromTo(link, {
        opacity: 0,
        yPercent: 20,
      }, 
      { 
        opacity: 1,
        yPercent: 0,
        ease: 'power1.out',
        duration: 0.6,
      }, delay + durationCharsText + 0.25);
    }

    if (info) {
      tl.fromTo(info, {
        opacity: 0,
        pointerEvents: 'none',
      }, {
        opacity: 1,
        ease: 'power1.out',
        duration: 0.5,
        overwrite: true,
      }, delay + durationCharsText);
      tl.set(info, {
        pointerEvents: '',
      });
    }

    if (magnetics.length) {
      tl.fromTo(magnetics, {
        opacity: 0,
        pointerEvents: 'none',
      }, {
        opacity: 1,
        ease: 'power1.out',
        duration: 0.5,
      }, delay + durationCharsText);
      tl.set(magnetics, {
        pointerEvents: '',
      });
    }

    return tl;
  }
}

window.Fake3dSlider = Fake3dSlider;